import { useMemo, useState } from "react";
import useSWR from "swr";
import { Input, message, Modal, Popover, Select, Table } from "antd";
import useSWRMutation from "swr/mutation";
import { API_ENDPOINTS, fetcher } from "../../util/api";
import dayjs from "dayjs";
import { useCurrency } from "../../hooks/useCurrency";
import { Copy, Eye, Loader, UploadCloud } from "lucide-react";
import { Button } from "../ui/button";
import { handleMutation } from "../../util/axiosConfig";
import uploadToS3 from "../../util/uploadToS3";
import { twMerge } from "tailwind-merge";
import { ColumnsType } from "antd/es/table";

type PayoutRecord = {
  id: string;
  fleetOwnerId: string;
  totalAmountPayable: string;
  amount: string;
  createdAt: string;
  paidAt: string | null;
  processedBy: string | null;
  status: string;
  settlementAccount: SettlementAccount;
  fleetOwner: FleetOwner;
}

interface PayoutRequestsProps {
  fleetOwnerId?: string;
}

function FleetOwnerPayoutRecords({
fleetOwnerId,
}: PayoutRequestsProps) {
  const {formatCurrency} = useCurrency();
  const [receiptImageUrl, setReceiptImageUrl] = useState('');
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [open, setOpen] = useState<boolean | undefined>();
  const [selectedFleetOwnerId, setSelectedFleetOwnerId] = useState(fleetOwnerId || '');

  const { data: payments=[], isLoading, mutate } = useSWR(
    fleetOwnerId
      ? `newAdmin/payment-record/${fleetOwnerId}`
      : "newAdmin/payment-records",
    fetcher
  );

  // Fetch data using SWR with axios fetcher
  const { data: fleetOwners=[], error, isLoading: loadingFleetOwners } = useSWR<FleetOwner[]>(
    API_ENDPOINTS.FLEET.ALL,
    fetcher,
  );

  const approvedFleetOwners: FleetOwner[] = useMemo(() => {
    if (!fleetOwners || fleetOwners.length === 0) return [];
    return fleetOwners.filter((owner) => owner.approved == "APPROVED");
  }, [fleetOwners]);


  const { trigger: processPayout, isMutating } = useSWRMutation("newAdmin/payout-records/manual", handleMutation);

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  }

  const handleProcessPayment = async () => {
    if (!receiptImageUrl) return;
    try {
      const response = await processPayout({
        receiptImageUrl,
        fleetOwnerId: selectedFleetOwnerId,
        amount: amount?.toString() || "0",
        paidAt: new Date().toISOString(),
      });
      message.success(response.message || "Success");
      setOpen(false);
      mutate();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }

  const handleReceiptUpload = async (file: File) => {
    if (!file) return

    setIsUploading(true)
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setReceiptImageUrl(url);
        },
        onError: (error: any) => {
          console.error("Upload failed:", error)
        },
      })
    } catch (error) {
      console.error("Upload error:", error)
    } finally {
      setIsUploading(false);
    }
  }  

  const columns: ColumnsType<PayoutRecord> = [
    {
      title: "Trucker",
      dataIndex: "fleetOwner",
      key: "fleetOwner",
      render: (_, record) => <div className="max-w-36 text-nowrap text-ellipsis overflow-hidden">{record?.fleetOwner?.companyName}</div>,
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, record) => (
        <div>
          {dayjs(record.createdAt).format('DD/MM/YYYY')}
        </div>
      ),
    },    
    {
      title: "Total Payable",
      dataIndex: "totalAmountPayable",
      key: "totalAmountPayable",
      render: (_, record) => (
        <div className="text-secondary">
          {formatCurrency(Number(record.totalAmountPayable))}
        </div>
      ),
    },
    {
      title: "Amount Paid",
      dataIndex: "amount",
      key: "amount",
      render: (_, record) => (
        <div className="text-primary">
          {formatCurrency(Number(record.amount))}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <div className={twMerge("font-semibold capitalize",
            record.status === "PENDING"
              ? 'text-secondary'
              : "text-success"
        )}>
          {record.status}
        </div>
      ),
    },
    {
      title: "Date Paid",
      dataIndex: "paidAt",
      key: "paidAt",
      render: (_, record) => (
        <div className="text-center">
          {record.paidAt ? dayjs(record.paidAt).format('DD/MM/YYYY') : "-"}
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "settlementAccount",
      key: "settlementAccount",
      render: (_, record) => (
        <Popover
          placement="bottomLeft"
          title="Settlement Account Details"
          content={<>
            <div className="flex flex-col items-start gap-1 p-4">
                <span>
                    Bank: {" "}
                    <strong className="font-medium">
                      {record.settlementAccount.bankName}
                    </strong>
                </span>
                <span>
                    Account Name: {" "}
                    <strong className="font-medium">
                      {record.settlementAccount.accountName}
                    </strong>
                </span>                            
                <span className="flex items-center gap-2">
                    Account Number: {" "}
                    <strong className="font-medium">{record.settlementAccount.accountNumber}</strong>
                    <Copy size={14} className="text-primary cursor-pointer" onClick={() => copyToClipboard(record.settlementAccount.accountNumber)} />
                </span>
            </div>          
          </>}
        >
          <Button className="text-white">
            <span>View</span>
            <Eye />
          </Button>
        </Popover>
      ),
    },       
    // {
    //   title: "Action",
    //   dataIndex: "action",
    //   key: "action",
    //   render: (_, record, index) => (
    //     <ConfirmModal
    //         triggerLabel="Record Payout"
    //         triggerClassName="text-white"
    //         open={open}
    //         disabled={record.status !== "PENDING"}
    //         disableConfirm={!receiptImageUrl}
    //         loading={isMutating && payoutRequestId === record.id}
    //         title="Upload Receipt (optional)"
    //         onConfirm={() => setPayoutRequestId(record.id)}
    //     >
    //         <div className="relative flex justify-center items-center">
    //             <input
    //                 type="file"
    //                 id="receiptImageUrl"
    //                 className="hidden"
    //                 accept="image/*"
    //                 onChange={(e) => {
    //                     const file = e.target.files?.[0]
    //                     if (file) {
    //                       handleReceiptUpload(file)
    //                     }
    //                 }}
    //             />
    //             <label
    //               htmlFor="receiptImageUrl"
    //               className={`flex items-center justify-center size-28 rounded border cursor-pointer ${receiptImageUrl ? "bg-blue-100 border-blue-300" : "bg-blue-50 border-blue-100"
    //                 }`}
    //             >
    //             {receiptImageUrl ? (
    //                 <img
    //                   src={receiptImageUrl || "/placeholder.svg"}
    //                   alt="Receipt preview"
    //                   className="w-full h-full object-contain"
    //                 />
    //             ) : (<span className="flex items-center justify-center">
    //                 {isUploading
    //                 ? (<Loader size={30} className="animate-spin text-primary" />)
    //                 : <>
    //                   <UploadCloud size={50} className="text-primary"  />
    //                 </>}
    //                 </span>)}
    //             </label>
    //         </div>
    //     </ConfirmModal>
    //   ),
    // }
  ];

  return (
    <div>
      <div className="flex items-center justify-end mb-6">
        <Button className="text-white" onClick={() => setOpen(true)}>
          Record Payout
        </Button>
        <Modal
          title="Record Payout"
          okText="Record Payout"
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={open}
          okButtonProps={{
            loading: isMutating
          }}
          onOk={handleProcessPayment}
          onCancel={() => setOpen(false)}
        >
          <>
            <div className="mb-4">
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                Amount Paid
              </label>
              <Input
                type="number"
                id="amount"
                placeholder="Amount paid"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value || 0))}
              />
            </div>
            {!selectedFleetOwnerId && (
              <div className="mb-4">
                <label htmlFor="fleetOwner" className="block text-sm font-medium text-gray-700 mb-1">
                  Fleet Owner
                </label>
                <Select
                  placeholder="Select fleet owner"
                  value={selectedFleetOwnerId}
                  onChange={(value) => {
                    setSelectedFleetOwnerId(value);
                  }}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option: any) =>
                    option?.children?.toLowerCase().indexOf(input.toLowerCase()) >=
                      0 ||
                    option?.value?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  loading={loadingFleetOwners}
                  className="w-full"
                >
                  {approvedFleetOwners?.map((owner) => {
                    return (
                      <Select.Option value={owner.id} key={owner.id}>
                        {owner.companyName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            )}
            <div>
              <label htmlFor="receiptImageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Upload Receipt (optional)
              </label>
              <div className="relative flex justify-center items-center">
                <input
                  type="file"
                  id="receiptImageUrl"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      handleReceiptUpload(file)
                    }
                  }}
                />
                <label
                  htmlFor="receiptImageUrl"
                  className={`flex items-center justify-center size-28 rounded border cursor-pointer ${receiptImageUrl ? "bg-blue-100 border-blue-300" : "bg-blue-50 border-blue-100"
                    }`}
                >
                {receiptImageUrl ? (
                    <img
                      src={receiptImageUrl || "/placeholder.svg"}
                      alt="Receipt preview"
                      className="w-full h-full object-contain"
                    />
                ) : (<span className="flex items-center justify-center">
                    {isUploading
                    ? (<Loader size={30} className="animate-spin text-primary" />)
                    : <>
                      <UploadCloud size={50} className="text-primary"  />
                    </>}
                    </span>)}
                </label>
              </div>
            </div>
          </>
        </Modal>
      </div>
      <Table
        columns={columns}
        dataSource={payments}
        loading={isLoading}
        rowKey="id"
      />
    </div>
  );
}

export default FleetOwnerPayoutRecords;
